import useSelectionStore from '@store/useSelectionStore';
import useGameStore from '@store/useGameStore';
import { ClickableType } from '@utils/enums';
import { CONFIG } from '@utils/constants';
import { getEffectiveStat } from '@utils/statCalculations';
import StatDisplay from './StatDisplay';
import UnitIcon from '@gameobjects/UnitIcon';
import HoverClickPopup from '@components/ui/general/HoverClickPopup';
import EffectBar from '@components/ui/game/state-indicator/EffectBar';
import StatBar from '@components/ui/general/StatBar';
import { Activity } from 'lucide-react';
import { STAT_ICONS } from '@utils/statIcons';
import { useT } from '@/i18n';
import './UnitInfoPanel.css';

export default function UnitInfoPanel() {
  const t = useT();

  // Use selector for proper reactivity when currentClick changes
  const currentClick = useSelectionStore(state => state.currentClick);
  // Subscribe to snapshot changes to update during cinematics
  const currentSnapshotIndex = useGameStore(state => state.currentSnapshotIndex);

  // Only show if a unit is selected
  if (!currentClick || currentClick.type !== ClickableType.UNIT) {
    return null;
  }

  // Get fresh unit data from game store
  const unit = useGameStore.getState().getUnitOrAvailableUnitById(currentClick.data?.id);

  if (!unit) {
    console.warn('[UnitInfoPanel] Unit not found:', currentClick.data?.id);
    return null;
  }

  // Validate unit has required data - FAIL LOUDLY
  if (!unit.unit_template) {
    throw new Error(`[UnitInfoPanel] Unit ${unit.id} missing unit_template`);
  }

  const template = unit.unit_template;

  // Validate required template fields
  if (template.max_hp === undefined || template.max_hp === null) {
    throw new Error(`[UnitInfoPanel] Unit template missing max_hp for unit ${unit.id}`);
  }
  if (template.max_ap === undefined || template.max_ap === null) {
    throw new Error(`[UnitInfoPanel] Unit template missing max_ap for unit ${unit.id}`);
  }

  // Calculate HP percentage
  if (unit.hp === undefined || unit.hp === null) {
    throw new Error(`[UnitInfoPanel] Unit ${unit.id} missing hp value`);
  }

  const currentHp = Math.floor(unit.hp);
  const maxHp = Math.floor(getEffectiveStat(unit, 'max_hp'));
  const hpPercentage = (currentHp / maxHp) * 100;

  // Get team color class
  if (!unit.teamClass) {
    throw new Error(`[UnitInfoPanel] Unit ${unit.id} missing teamClass`);
  }
  const teamClass = unit.teamClass;

  // Get template name (without ID suffix)
  const templateName = template.name;

  // Build HP popup content
  const hpPopupContent = (
    <div className="hp-popup">
      <div className="hp-popup-header">
        <Activity size={16} />
        <span>{t('unit_info.health_status')}</span>
      </div>

      <div className="hp-popup-content">
        <div className="hp-stat-row">
          <span>{t('unit_info.current_hp')}</span>
          <span className="hp-value">{currentHp}</span>
        </div>
        <div className="hp-stat-row">
          <span>{t('unit_info.max_hp')}</span>
          <span className="hp-value">{maxHp}</span>
        </div>
        {currentHp < maxHp && (
          <>
            <div className="hp-separator" />
            <div className="hp-stat-row damage">
              <span>{t('unit_info.damage_taken')}</span>
              <span className="hp-value">-{maxHp - currentHp}</span>
            </div>
            <div className="hp-stat-row">
              <span>{t('unit_info.health_percent')}</span>
              <span className="hp-value">{Math.floor(hpPercentage)}%</span>
            </div>
          </>
        )}
      </div>
    </div>
  );

  // Build HP bar element
  const hpBarElement = (
    <StatBar
      value={currentHp}
      maxValue={maxHp}
      colorMode="tier"
      label={t('unit_info.hp_label')}
      textColor="#fff"
      className="hp-bar"
    />
  );

  // Check if unit has active effects
  const hasEffects = unit.active_effects && unit.active_effects.length > 0;

  return (
    <div className="unit-info-card">
      <div className="unit-info-header">
        <UnitIcon unitData={unit} size="big" />
        <div className="unit-info-header-content">
          <div className="unit-title-row">
            <h3 className={`unit-title team-${teamClass}`}>{templateName}</h3>
            <span className="unit-id">#{unit.id}</span>
          </div>
          {CONFIG.SHOW_COORD && (
            <div className="unit-position">
              <span>({unit.x}, {unit.y})</span>
            </div>
          )}
          {hasEffects && <EffectBar unitId={unit.id} />}
        </div>
      </div>

      {/* HP Bar with popup */}
      <HoverClickPopup
        trigger={hpBarElement}
        className="hp-popup-container"
      >
        {hpPopupContent}
      </HoverClickPopup>

      {/* Stats Container - 2 rows layout */}
      <div className="stats-container">
        {/* Row 1: Combat stats (Physical left, Mental right) */}
        <div className="stats-row stats-row-combat">
          <div className="stat-group stat-group-physical">
            <StatDisplay
              unit={unit}
              statName="strength"
              icon={<STAT_ICONS.strength size={16} />}
              tooltip={t('unit_info.tooltip_strength')}
            />
            <StatDisplay
              unit={unit}
              statName="armor"
              icon={<STAT_ICONS.armor size={16} />}
              tooltip={t('unit_info.tooltip_armor')}
            />
          </div>
          <div className="stat-group stat-group-mental">
            <StatDisplay
              unit={unit}
              statName="psi"
              icon={<STAT_ICONS.psi size={16} />}
              tooltip={t('unit_info.tooltip_psi')}
            />
            <StatDisplay
              unit={unit}
              statName="will"
              icon={<STAT_ICONS.will size={16} />}
              tooltip={t('unit_info.tooltip_will')}
            />
          </div>
        </div>

        {/* Row 2: Utility stats (centered) */}
        <div className="stats-row stats-row-utility">
          <StatDisplay
            unit={unit}
            statName="vision"
            icon={<STAT_ICONS.vision size={16} />}
            tooltip={t('unit_info.tooltip_vision')}
          />
          <StatDisplay
            unit={unit}
            statName="agility"
            icon={<STAT_ICONS.agility size={16} />}
            tooltip={t('unit_info.tooltip_agility')}
          />
          <StatDisplay
            unit={unit}
            statName="movement"
            icon={<STAT_ICONS.movement size={16} />}
            tooltip={t('unit_info.tooltip_movement')}
          />
        </div>
      </div>
    </div>
  );
}